'use client';

import { useState, useRef } from 'react';
import { Save, Mail, Image as ImageIcon, Code, Palette, Type, LayoutGrid, Download } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { emailTemplateBuilderContent } from '../../data/email-template-builder-content';

type TemplateBlock = {
    id: string;
    type: 'text' | 'image' | 'button' | 'divider' | 'spacer';
    content: string;
    styles: Record<string, string>;
};

export default function EmailTemplateBuilder() {
    const [templateName, setTemplateName] = useState('My Email Template');
    const [activeTab, setActiveTab] = useState('build');
    const [blocks, setBlocks] = useState<TemplateBlock[]>([
        {
            id: '1',
            type: 'text',
            content: 'Hello, {{name}}!',
            styles: {
                fontSize: '24px',
                color: '#333333',
                textAlign: 'center',
                padding: '20px 0',
                fontFamily: 'Arial, sans-serif'
            }
        },
        {
            id: '2',
            type: 'divider',
            content: '',
            styles: {
                height: '1px',
                backgroundColor: '#e0e0e0',
                margin: '20px 0'
            }
        },
        {
            id: '3',
            type: 'text',
            content: 'Thank you for signing up!',
            styles: {
                fontSize: '16px',
                color: '#666666',
                padding: '10px 0',
                fontFamily: 'Arial, sans-serif'
            }
        }
    ]);
    const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
    const [htmlCode, setHtmlCode] = useState('');
    const [isPreview, setIsPreview] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const addBlock = (type: TemplateBlock['type']) => {
        const newBlock: TemplateBlock = {
            id: Date.now().toString(),
            type,
            content: '',
            styles: {
                width: '100%',
                margin: '10px 0',
                ...(type === 'text' && {
                    content: 'New Text Block',
                    fontSize: '16px',
                    color: '#333333',
                    fontFamily: 'Arial, sans-serif'
                }),
                ...(type === 'button' && {
                    content: 'Click Me',
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    textDecoration: 'none',
                    fontFamily: 'Arial, sans-serif'
                }),
                ...(type === 'divider' && {
                    height: '1px',
                    backgroundColor: '#e0e0e0',
                    margin: '20px 0'
                }),
                ...(type === 'spacer' && {
                    height: '30px',
                    backgroundColor: 'transparent'
                }),
                ...(type === 'image' && {
                    content: 'https://via.placeholder.com/600x200',
                    maxWidth: '100%',
                    height: 'auto'
                })
            }
        };
        setBlocks([...blocks, newBlock]);
        setSelectedBlock(newBlock.id);
    };

    const updateBlock = (id: string, updates: Partial<TemplateBlock>) => {
        setBlocks(blocks.map(block =>
            block.id === id ? { ...block, ...updates } : block
        ));
    };

    // Helper function to safely update the currently selected block
    const updateSelectedBlock = (updates: Partial<TemplateBlock>) => {
        if (selectedBlock) {
            updateBlock(selectedBlock, updates);
        }
    };

    const deleteBlock = (id: string) => {
        setBlocks(blocks.filter(block => block.id !== id));
        if (selectedBlock === id) {
            setSelectedBlock(null);
        }
    };

    const moveBlock = (id: string, direction: 'up' | 'down') => {
        setBlocks(prevBlocks => {
            const index = prevBlocks.findIndex(block => block.id === id);
            if (index === -1) return prevBlocks;

            const newBlocks = [...prevBlocks];
            if (direction === 'up' && index > 0) {
                [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
            } else if (direction === 'down' && index < newBlocks.length - 1) {
                [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
            } else {
                return prevBlocks; // No change needed
            }
            return newBlocks;
        });
    };

    const generateHtml = () => {
        // This is a simplified version - in a real app, you'd want a more robust HTML generator
        let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${templateName}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
          }
          .button {
            display: inline-block;
            padding: 10px 20px;
            background-color: #4f46e5;
            color: white;
            text-decoration: none;
            border-radius: 4px;
          }
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body style="margin: 0; padding: 0;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            <td style="padding: 20px;">
    `;

        blocks.forEach(block => {
            const style = Object.entries(block.styles)
                .map(([key, value]) => `${key}: ${value};`)
                .join(' ');

            switch (block.type) {
                case 'text':
                    html += `
            <div style="${style}">
              ${block.content}
            </div>
          `;
                    break;
                case 'image':
                    html += `
            <div style="text-align: center; margin: 20px 0;">
              <img src="${block.content}" alt="" style="${style}" />
            </div>
          `;
                    break;
                case 'button':
                    html += `
            <div style="text-align: center; margin: 20px 0;">
              <a href="#" class="button" style="${style}">
                ${block.content}
              </a>
            </div>
          `;
                    break;
                case 'divider':
                    html += `
            <div style="${style}"></div>
          `;
                    break;
                case 'spacer':
                    html += `
            <div style="${style}"></div>
          `;
                    break;
            }
        });

        html += `
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;

        return html;
    };

    const handlePreview = () => {
        setHtmlCode(generateHtml());
        setIsPreview(true);
    };

    const handleDownload = () => {
        const element = document.createElement('a');
        const file = new Blob([generateHtml()], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        element.download = `${templateName.replace(/\s+/g, '-').toLowerCase()}.html`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            if (selectedBlock) {
                updateBlock(selectedBlock, {
                    content: event.target?.result as string,
                    type: 'image'
                });
            }
        };
        reader.readAsDataURL(file);
    };

    const selectedBlockData = blocks.find(block => block.id === selectedBlock);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                <ToolPageIntro title="Email Template Builder">
                    Build HTML email templates with drag-and-drop blocks: text, images, buttons, dividers. Customize styles and preview before copying code. Perfect for newsletters and transactional emails. No sign-up.
                </ToolPageIntro>
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Left sidebar - Blocks */}
                    <div className="w-full md:w-64 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Blocks</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => addBlock('text')}
                                className="w-full flex items-center gap-2 p-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                                <Type size={16} /> Text
                            </button>
                            <button
                                onClick={() => addBlock('image')}
                                className="w-full flex items-center gap-2 p-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                                <ImageIcon size={16} /> Image
                            </button>
                            <button
                                onClick={() => addBlock('button')}
                                className="w-full flex items-center gap-2 p-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                                <LayoutGrid size={16} /> Button
                            </button>
                            <button
                                onClick={() => addBlock('divider')}
                                className="w-full flex items-center gap-2 p-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                                <div className="w-4 h-0.5 bg-gray-400"></div>
                                Divider
                            </button>
                            <button
                                onClick={() => addBlock('spacer')}
                                className="w-full flex items-center gap-2 p-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            >
                                <div className="w-4 h-4 border border-dashed border-gray-400"></div>
                                Spacer
                            </button>
                        </div>
                    </div>

                    {/* Main content - Builder */}
                    <div className="flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                            {/* Toolbar */}
                            <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <Mail size={20} className="text-purple-600" />
                                    <input
                                        type="text"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        className="text-xl font-semibold bg-transparent border-none focus:ring-2 focus:ring-purple-500 rounded px-2 py-1"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handlePreview}
                                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md flex items-center gap-2"
                                    >
                                        <Code size={16} /> {isPreview ? 'Edit' : 'Code'}
                                    </button>
                                    <button
                                        onClick={handleDownload}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md flex items-center gap-2"
                                    >
                                        <Download size={16} /> Download
                                    </button>
                                </div>
                            </div>

                            {/* Preview/Edit Area */}
                            <div className="p-6">
                                {isPreview ? (
                                    <div className="bg-white p-4 border rounded">
                                        <pre className="text-xs overflow-auto max-h-[600px] p-4 bg-gray-50 dark:bg-gray-900 rounded">
                                            <code className="text-gray-800 dark:text-gray-200">
                                                {htmlCode}
                                            </code>
                                        </pre>
                                    </div>
                                ) : (
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 min-h-[600px] bg-white dark:bg-gray-900">
                                        {blocks.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                                <Mail size={48} className="mb-4 text-gray-300" />
                                                <p>Drag and drop blocks from the left or click to add</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {blocks.map((block) => (
                                                    <div
                                                        key={block.id}
                                                        onClick={() => setSelectedBlock(block.id)}
                                                        className={`relative p-4 border-2 rounded-lg transition-all ${selectedBlock === block.id
                                                            ? 'border-purple-500 bg-purple-50 dark:bg-gray-800/50'
                                                            : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                                                            }`}
                                                    >
                                                        {block.type === 'text' && (
                                                            <div
                                                                style={block.styles}
                                                                contentEditable
                                                                onBlur={(e) =>
                                                                    updateBlock(block.id, {
                                                                        content: e.currentTarget.innerHTML
                                                                    })
                                                                }
                                                                dangerouslySetInnerHTML={{ __html: block.content }}
                                                                className="outline-none min-h-[24px] w-full"
                                                            />
                                                        )}

                                                        {block.type === 'image' && (
                                                            <div className="relative">
                                                                <img
                                                                    src={block.content || 'https://via.placeholder.com/600x200'}
                                                                    alt=""
                                                                    style={block.styles}
                                                                    className="mx-auto"
                                                                />
                                                                <input
                                                                    type="file"
                                                                    ref={fileInputRef}
                                                                    onChange={handleImageUpload}
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                />
                                                            </div>
                                                        )}

                                                        {block.type === 'button' && (
                                                            <div
                                                                className="text-center"
                                                                contentEditable
                                                                suppressContentEditableWarning={true}
                                                                onBlur={(e) =>
                                                                    updateBlock(block.id, {
                                                                        content: e.currentTarget.textContent || ''
                                                                    })
                                                                }
                                                            >
                                                                <a
                                                                    href="#"
                                                                    style={block.styles}
                                                                    className="inline-block px-6 py-2 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                                                >
                                                                    {block.content}
                                                                </a>
                                                            </div>
                                                        )}

                                                        {block.type === 'divider' && (
                                                            <div style={block.styles}></div>
                                                        )}

                                                        {block.type === 'spacer' && (
                                                            <div style={block.styles}></div>
                                                        )}

                                                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveBlock(block.id, 'up');
                                                                }}
                                                                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                                disabled={blocks.findIndex(b => b.id === block.id) === 0}
                                                            >
                                                                ↑
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveBlock(block.id, 'down');
                                                                }}
                                                                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                                disabled={blocks.findIndex(b => b.id === block.id) === blocks.length - 1}
                                                            >
                                                                ↓
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteBlock(block.id);
                                                                }}
                                                                className="p-1 text-red-500 hover:text-red-700"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Right sidebar - Block settings */}
                    {/* Right sidebar - Block settings */}
                    {selectedBlockData && !isPreview && (
                        <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
                                {selectedBlockData.type.charAt(0).toUpperCase() + selectedBlockData.type.slice(1)} Settings
                            </h3>

                            <div className="space-y-4">
                                {selectedBlockData.type === 'text' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Text Content
                                        </label>
                                        <textarea
                                            value={selectedBlockData.content}
                                            onChange={(e) =>
                                                updateSelectedBlock({ content: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border rounded-md"
                                            rows={3}
                                        />
                                    </div>
                                )}

                                {selectedBlockData.type === 'image' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Image URL
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedBlockData.content}
                                            onChange={(e) =>
                                                updateSelectedBlock({ content: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border rounded-md"
                                        />
                                    </div>
                                )}

                                {/* Font Size */}
                                {selectedBlockData.styles.fontSize && (
                                    <div>
                                        <label className="block text-xs mb-1">Font Size</label>
                                        <input
                                            type="range"
                                            min="10"
                                            max="48"
                                            value={parseInt(selectedBlockData.styles.fontSize)}
                                            onChange={(e) =>
                                                updateSelectedBlock({
                                                    styles: {
                                                        ...selectedBlockData.styles,
                                                        fontSize: `${e.target.value}px`
                                                    }
                                                })
                                            }
                                            className="w-full"
                                        />
                                    </div>
                                )}

                                {/* Text Color */}
                                {selectedBlockData.styles.color && (
                                    <div>
                                        <label className="block text-xs mb-1">Text Color</label>
                                        <input
                                            type="color"
                                            value={selectedBlockData.styles.color}
                                            onChange={(e) =>
                                                updateSelectedBlock({
                                                    styles: {
                                                        ...selectedBlockData.styles,
                                                        color: e.target.value
                                                    }
                                                })
                                            }
                                            className="w-full h-8"
                                        />
                                    </div>
                                )}

                                {/* Button Background */}
                                {selectedBlockData.type === 'button' && (
                                    <div>
                                        <label className="block text-xs mb-1">
                                            Background Color
                                        </label>
                                        <input
                                            type="color"
                                            value={selectedBlockData.styles.backgroundColor}
                                            onChange={(e) =>
                                                updateSelectedBlock({
                                                    styles: {
                                                        ...selectedBlockData.styles,
                                                        backgroundColor: e.target.value
                                                    }
                                                })
                                            }
                                            className="w-full h-8"
                                        />
                                    </div>
                                )}

                                {/* Alignment */}
                                <div>
                                    <label className="block text-xs mb-1">Alignment</label>
                                    <div className="flex gap-2">
                                        {['left', 'center', 'right'].map((align) => (
                                            <button
                                                key={align}
                                                onClick={() =>
                                                    updateSelectedBlock({
                                                        styles: {
                                                            ...selectedBlockData.styles,
                                                            textAlign: align
                                                        }
                                                    })
                                                }
                                                className={`flex-1 py-1 text-xs rounded ${selectedBlockData.styles.textAlign === align
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            >
                                                {align}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Delete */}
                                <div className="pt-2 border-t">
                                    <button
                                        onClick={() =>
                                            selectedBlock && deleteBlock(selectedBlock)
                                        }
                                        className="w-full py-2 text-white bg-red-600 rounded-md"
                                    >
                                        Delete Block
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={emailTemplateBuilderContent.title}
                    description={emailTemplateBuilderContent.description}
                    content={emailTemplateBuilderContent.content}
                    faqs={emailTemplateBuilderContent.faqs}
                    relatedTools={emailTemplateBuilderContent.relatedTools}
                />
            </div>
        </div>
    );
}
